import { extractErrorDetails, type Result } from '@src/services/common/Result';
import { LogManager } from '@src/utility/Logger';

import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { ElementState } from '@src/renderer/ElementState';
import type { VideoSourceStore } from '@src/stores-v2/VideoSourceStore';

export interface SplitResultSelectionTarget {
  elementId: string | null; // id to select (may be null)
}

// Structural types (decoupled from concrete store classes)
export interface EditorStoreLike {
  splitElement: (id: string, time: number) => Array<{ id: string }> | null;
  editor: {
    timeline: IVideoEditor['timeline'];
  };
}

export interface VideoCreatorStoreLike {
  time: number;
  getActiveElement: () => ElementState | undefined;
  lockAwareSetActiveElement: (id: string) => void;
}

export interface SplitElementServiceDeps {
  editorStore: EditorStoreLike;
  videoCreator: VideoCreatorStoreLike;
  videoSource: VideoSourceStore; // scheduling selection after state change
}

/**
 * Splits currently active element (layout aware) at playhead.
 * Provides keyboard shortcut predicate (Ctrl/Cmd + K, excluding Shift/Alt).
 * Stateless; selection scheduling performed via VideoSourceStore.onNextStateChange.
 */
export class SplitElementService {
  private readonly _editorStore: EditorStoreLike;
  private readonly _videoCreator: VideoCreatorStoreLike;
  private readonly _videoSource: VideoSourceStore;
  private readonly _logger = LogManager.getLogger(
    'service:SplitElementService',
  );

  constructor(deps: SplitElementServiceDeps) {
    this._editorStore = deps.editorStore;
    this._videoCreator = deps.videoCreator;
    this._videoSource = deps.videoSource;
  }

  async splitActiveTrackElementAtCurrentTime(
    activeTracks: number[],
  ): Promise<Result<SplitResultSelectionTarget>> {
    const time = this._videoCreator.time;
    const element = this._editorStore.editor.timeline.allElements.find(
      el =>
        el.track != null &&
        activeTracks.includes(el.track) &&
        el.time < time &&
        el.end_time > time,
    );
    if (!element) {
      return {
        ok: false,
        error: {
          code: 'NOT_FOUND',
          message: 'No element at playhead on active tracks',
        },
      };
    }

    return this.splitElementAtTime(element.id, time);
  }

  private async splitElementAtTime(
    elementId: string,
    time: number,
  ): Promise<Result<SplitResultSelectionTarget>> {
    try {
      let elementToSelect: string | null = null;
      const resultElements = this._editorStore.splitElement(elementId, time);
      elementToSelect = resultElements?.[0]?.id ?? null;
      if (elementToSelect) {
        const id: string = elementToSelect; // narrow non-null for callback usage
        this._videoSource.onNextStateChange(() => {
          this._videoCreator.lockAwareSetActiveElement(id);
        });
      }
      return { ok: true, value: { elementId: elementToSelect } };
    } catch (err: unknown) {
      this._logger.error('Failed to split element', { err });
      return {
        ok: false,
        error: {
          code: 'UNKNOWN',
          message: 'Unexpected split failure',
          details: extractErrorDetails(err),
        },
      };
    }
  }
}
